import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookmarkPlus, Camera, LayoutGrid } from 'lucide-react';
import { api } from '@onvice/portal-shell-api';

interface Props {
  /** Widget-Slug aus widget-registry, z.B. 'aufgaben.embed'. */
  widgetSlug: string;
  /** Default-Name fuer den neuen Tab im Eingabefeld. */
  defaultName: string;
  /** Icon-Name (Lucide) fuer den Tab in der Bar. */
  icon?: string;
  /** Optionaler Filter-Snapshot — wenn gesetzt, wird er bei Snapshot-Klon
   *  als config.filter eingebrannt. Sonst startet Snapshot mit Modul-Default. */
  currentFilters?: Record<string, unknown>;
  /** Knopf-Variante: 'pill' (kompakt, fuer Sub-Tab-Bars) oder 'header'
   *  (groesser, fuer Page-Header ohne Sub-Tab-Bar). Default 'pill'. */
  variant?: 'pill' | 'header';
}

/**
 * „Als Tab uebernehmen" — Knopf + Modal fuer den Klon einer Modul-Sicht in
 * eine persoenliche Page (Joerg 2026-05-29).
 *
 * Modus:
 *   - Snapshot: kompakter Embed (h=10), Filter eingefroren ueber config.
 *     Eingehender Filter-State wird (sofern uebergeben) als Default
 *     eingebrannt. Wo das Modul-Widget readonly-Mode unterstuetzt, wird die
 *     Filter-UI dort versteckt; sonst rendert es wie der Voll-Tab.
 *   - Voll-Tab: grosser Embed (h=20), keine Config — Verhalten wie der
 *     bisherige Modul-Tab.
 */
export default function ClonModuleButton({ widgetSlug, defaultName, icon, currentFilters, variant = 'pill' }: Props) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState(defaultName);

  async function doClone(mode: 'full' | 'snapshot') {
    if (busy) return;
    setBusy(true);
    try {
      const config = mode === 'snapshot' && currentFilters ? { filter: currentFilters } : undefined;
      const r = await api.post<{ success: boolean; data: { id: string } }>(
        '/portal/my-pages/clone-module',
        {
          name: name.trim() || defaultName,
          widgetSlug,
          mode,
          icon: icon ?? 'CheckSquare',
          ...(config ? { config } : {}),
        },
      );
      if (r.success) {
        window.dispatchEvent(new CustomEvent('my-pages:changed'));
        navigate(`/uebersicht/page/${r.data.id}`);
      }
    } catch (e) {
      console.error('Klon fehlgeschlagen:', e);
    } finally {
      setBusy(false);
      setOpen(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          variant === 'header'
            ? 'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm bg-brand-600/10 text-brand-300 hover:bg-brand-600/20 border border-brand-600/40 min-h-[40px]'
            : 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-brand-600/10 text-brand-300 hover:bg-brand-600/20 border border-brand-600/40'
        }
        title="Diese Modul-Sicht als persoenliche Seite uebernehmen"
      >
        <BookmarkPlus size={14} /> Als Tab übernehmen
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => !busy && setOpen(false)}>
          <div className="bg-card border border-default rounded-xl max-w-md w-full p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold">Als persönliche Seite übernehmen</h3>
            <label className="block">
              <span className="text-xs text-muted-foreground">Name des neuen Tabs</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-lg bg-background border border-default text-foreground"
                autoFocus
              />
            </label>
            <div className="grid grid-cols-1 gap-2">
              <button
                type="button"
                onClick={() => doClone('snapshot')}
                disabled={busy}
                className="flex items-start gap-3 p-3 rounded-lg border border-default hover:bg-muted text-left transition-colors disabled:opacity-50"
              >
                <Camera className="mt-0.5 text-brand-400 shrink-0" size={18} />
                <div>
                  <div className="font-medium">Snapshot</div>
                  <div className="text-xs text-muted-foreground">Kompakt, Filter eingefroren. Mehrere Snapshots auf einer Seite kombinierbar.</div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => doClone('full')}
                disabled={busy}
                className="flex items-start gap-3 p-3 rounded-lg border border-default hover:bg-muted text-left transition-colors disabled:opacity-50"
              >
                <LayoutGrid className="mt-0.5 text-brand-400 shrink-0" size={18} />
                <div>
                  <div className="font-medium">Voll-Tab</div>
                  <div className="text-xs text-muted-foreground">Komplette Modul-Sicht mit allen Funktionen — wie der Modul-Tab.</div>
                </div>
              </button>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setOpen(false)}
                disabled={busy}
                className="px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:text-foreground"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
